import {
    InterfaceFactoryOptions,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import { GraphicsRenderComponent, IGraphicsOptions } from '@tencent/alloy-graphics';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import ImageQuickLookManager from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-quick-look-manager';
import {
    ForegroundComponent,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/foreground-component';
import { IReactionDisposer, reaction } from 'mobx';
import {
    generateGraphicsModel,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/graphic-adapter/generate-graphics-model';
import { RenderPostionType } from '@src/js/constant';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import debounce from 'lodash/debounce';
import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';

const DEFAULT_DURATION = 66666; // 默认15帧
const DEBOUNCE_LOOP_TIME = 100;
const DEFAULT_SET_TIME = 40;
const DURATION_SCALE = 1000;

export class GifForegroundComponent extends ForegroundComponent {
    /**
     * gif当前帧的索引
     * @private
     */
    private frameIndex = 0;

    /**
     * 当前帧，当前帧需要缓存，比如缩放、拖拽、缩略图会重复使用当前帧
     * @private
     */
    private currentVideoFrame?: VideoFrame;

    /**
     * 记录上一帧，渲染当前帧后释放上一帧显存
     * @private
     */
    private lastVideoFrame?: VideoFrame;

    /**
     * 播放间隔
     * @private
     */
    private duration = 0;

    /**
     * 全部帧总量
     * @private
     */
    private frameCount = 0;

    /**
     * gif解码器
     * @private
     */
    private gifDecoder?: ImageDecoder;

    /**
     * 定时任务id
     * @private
     */
    private timerId: any;

    /**
     * 回收标志
     * @private
     */
    private disposed = false;

    /**
     * 手动播放、暂停标志，用于用户播放行为
     * @private
     */
    private userPlayFlag = true;

    /**
     * 当前资源的gif路径，可能是cdn/秒看blob
     * @private
     */
    private gifUrl?: string;

    /**
     * important
     * 是否在发起渲染中
     * 由于alloyRender的渲染是异步的，经常会出现在回收帧显存时，渲染还未发生的情况
     * 因此给一个阻塞，代表是否调用了渲染，但渲染未结束
     * 在这个期间，禁止回收帧和解码器
     * @private
     */
    private requestRender = false;

    private needFrameExtract: boolean;
    private operationReaction?: IReactionDisposer;
    private slideChangeReaction?: IReactionDisposer;
    private slidePlayerChangeReaction?: IReactionDisposer;
    private slidePlayReaction?: IReactionDisposer;
    private gifPlayReaction?: IReactionDisposer;

    public constructor(factoryOptions: InterfaceFactoryOptions<IPictureModel | IShapeModel>) {
        super(factoryOptions);
        this.initOperationReaction();
        this.initSlideChangeReaction();
        this.initSlidePlayStateChange();
        this.initPlayerChangeReaction();
        this.initGifPlayStateChange();
    }

    /**
     * 是否是缩略图
     */
    public isThumbnailGifComponent = () => this.factoryOptions.region !== RenderPostionType.CANVAS
            && this.factoryOptions.region !== RenderPostionType.PLAYER;

    /**
     * 渲染GIF前景图片，上层调用，非自循环
     * 区别在于上层调用不需要获取下一帧，复用当前帧渲染，例如缩放旋转导致的刷新
     */
    public async renderComponent() {
        // 渲染前，先暂停当前gif渲染任务（如有）
        this.pauseGifLoopRender();

        // 目前缩略图走不到thumbnail这里，还是用的老一套gif；先做了这个能力，未来再看看有必要切过来没有
        // 两个问题：1) 只有编辑区和播放区需要动态gif；2) 自己接管解码太占内存，不如交给chrome
        if (
            this.isThumbnailGifComponent()
        ) {
            this.renderThumbnailGifComponent();
            return;
        }

        // 现在上层有切页前的预加载，先不预渲染gif，避免有一些比较大的gif，todo: 考虑加一些条件，比如大gif才不预渲染
        if (!this.isCurrentRegionGifModel()) return;
        const style = this.generateForegroundStyle();
        const graphicsModel = await this.getCurrentFrameGraphicsModel(style);

        // 没有帧缓存就解码出一帧，否则复用缓存
        if (!this.currentVideoFrame) {
            await this.setFrameInGraphicModel(graphicsModel, this.frameIndex);
        }

        if (this.app) {
            this.app.allRefresh = true;
        }

        this.buildGifComponent(graphicsModel);
        this.debounceStartLoopGif();
    }

    /**
     * 渲染缩略图gif
     * 区别在于缩略图gif不需要循环渲染
     */
    public renderThumbnailGifComponent = async () => {
        const style = this.generateForegroundStyle();
        const graphicsModel = await this.generateGifGraphicsModel(style) as IGraphicsOptions;
        if (this.currentVideoFrame) {
            if (graphicsModel.fillStyle?.imageFill) {
                graphicsModel.fillStyle.imageFill.gifFrameResource = this.currentVideoFrame;
            }
        } else {
            await this.setFrameInGraphicModel(graphicsModel, 0);
        }

        this.buildGifComponent(graphicsModel);

        // 复制一份首帧图给封面图，否则释放解码器时，首帧图会一起被释放掉，再更新缩略图时就拿不到了
        const { currentVideoFrame } = this;
        this.currentVideoFrame = currentVideoFrame?.clone();

        // 缩略图渲染完首帧之后直接释放资源
        this.resetGifInfo();
    };

    /**
     * gif组件创建/更新
     * @param graphicsModel
     */
    public buildGifComponent = (graphicsModel: IGraphicsOptions) => {
        if (!this.foregroundGraphics) {
            this.foregroundGraphics = new GraphicsRenderComponent(graphicsModel);
            this.addChild(this.foregroundGraphics);
        } else {
            this.foregroundGraphics?.updateModel(graphicsModel);
        }

        this.dealFlip();
        this.dealVisibleStatus();
    };

    /**
     * gif的循环自渲染
     */
    public loopRenderComponent = async () => {
        // 如果是首次加载，就先构造
        if (!this.foregroundGraphics) {
            await this.renderComponent();
        } else {
            // 非首次加载，直接更新
            const style = this.generateForegroundStyle();
            const graphicsModel = await this.generateGifGraphicsModel(style) as IGraphicsOptions;

            // 根据帧下标获取对应帧
            await this.setFrameInGraphicModel(graphicsModel, this.frameIndex);

            // 帧循环自增
            const addIndex = this.needFrameExtract ? 2 : 1;
            this.frameIndex = this.frameIndex + addIndex;
            if (this.frameIndex >= this.frameCount) this.frameIndex = 0;

            // 目前stable层的autoRender可能是关闭的，所以如果需要播放母版gif，要把autoRender打开，渲染完这帧再还原
            const { app } = this.foregroundGraphics;
            const autoRender = !!app?.autoRender;
            if (app) {
                app.autoRender = true;
            }

            this.foregroundGraphics?.updateModel(graphicsModel);
            if (app) {
                app.autoRender = autoRender;
            }
        }
    };

    /**
     * 获取解码器
     * @param gifUrl
     */
    public getGifDecoder = async (gifUrl?: string): Promise<ImageDecoder | undefined> => {
        if (this.gifDecoder) {
            await this.getDecoderInfo();
            return this.gifDecoder;
        }

        if (gifUrl && this.factoryOptions.gifService) {
            try {
                this.gifDecoder = await this.factoryOptions.gifService.getGifDecoderByCache(gifUrl);
            } catch (e) {
                this.gifDecoder = await this.factoryOptions.gifService.getImageDecoderByUrl(gifUrl);
                window.log.error(['get decoder error'], e, this.gifUrl);
            }

            // 等待 imageDecoder 初始化完成
            await this.getDecoderInfo();
            return this.gifDecoder;
        }
    };

    /**
     * 获取解码基础信息
     */
    public async getDecoderInfo() {
        if (this.gifDecoder?.tracks.selectedTrack) {
            await this.gifDecoder.tracks.ready;
            await this.gifDecoder.completed;
            this.frameCount = this.gifDecoder.tracks.selectedTrack.frameCount || 0;
            const gifFrameResource = await this.getGifFrameResourceByIndex(this.gifDecoder, this.frameIndex);
            this.updateDuration(gifFrameResource);
        }
    }

    /**
     * 根据style生成graphics构造所需的model
     * @param style
     */
    // eslint-disable-next-line complexity
    public generateGifGraphicsModel = (style): IGraphicsOptions => {
        const elementModel = this.factoryOptions.model;
        const frameOnSlide = elementModel.getFrame?.().frameOnSlide();
        const realRotateAndFlip = {
            rotate: frameOnSlide.rotate,
            flipH: frameOnSlide.flipH,
            flipV: frameOnSlide.flipV,
        };

        return generateGraphicsModel({
            style,
            factoryOptions: this.factoryOptions,
            realRotateAndFlip,
        });
    };

    public getCurrentFrameGraphicsModel = (style): IGraphicsOptions => {
        const gifGraphicsModel = this.generateGifGraphicsModel(style);
        this.reuseCurrentFrame(gifGraphicsModel);
        return gifGraphicsModel;
    };

    /**
     * 获取下一帧
     * @param gifGraphicsModel
     * @param frameIndex
     */
    public setFrameInGraphicModel = async (gifGraphicsModel: IGraphicsOptions, frameIndex: number) => {
        const gifUrl = (this.factoryOptions.model as IPictureModel).props.src;
        if (this.gifUrl && this.gifUrl !== gifUrl) {
            this.resetGifInfo();
            this.frameIndex = 0;
        }

        this.gifUrl = gifUrl;
        await this.getGifDecoder(this.gifUrl);
        if (this.factoryOptions.gifService && this.gifDecoder?.tracks?.selectedTrack) {
            const gifFrameResource = await this.getGifFrameResourceByIndex(this.gifDecoder, frameIndex);
            this.updateDuration(gifFrameResource);
            if (gifGraphicsModel.fillStyle?.imageFill) {
                gifGraphicsModel.fillStyle.imageFill.gifFrameResource = gifFrameResource;
                if (gifFrameResource !== this.currentVideoFrame) {
                    this.currentVideoFrame?.close();
                    this.currentVideoFrame = gifFrameResource;
                }
            }
        }

        this.reuseCurrentFrame(gifGraphicsModel);
    };

    public onDidRender() {
        super.onDidRender();
        this.lastVideoFrame?.close();
        this.requestRender = false;
        if (this.disposed) {
            requestAnimationFrame(() => {
                this.gifDecoder?.close();
            });
        }
    }

    public dispose = () => {
        super.dispose();
        this.disposed = true;
        if (this.foregroundGraphics) {
            this.removeChild(this.foregroundGraphics);
        }
        this.pauseGifLoopRender();
        this.operationReaction?.();
        this.slideChangeReaction?.();
        this.slidePlayerChangeReaction?.();
        this.slidePlayReaction?.();
        this.gifPlayReaction?.();
        this.currentVideoFrame?.close?.();
        this.clearImageDecoder();
        if (!this.requestRender) {
            this.currentVideoFrame?.close?.();
            this.currentVideoFrame = undefined;
        }
    };

    public pauseGifLoopRender = () => {
        clearTimeout(this.timerId);
        this.timerId = undefined;
    };

    private startLoopGif = async () => {
        if (this.canLoopGif()) {
            clearTimeout(this.timerId);
            this.requestRender = true;
            this.loopRenderComponent();
            this.timerId = setTimeout(() => {
                this.startLoopGif();
            }, this.duration / DURATION_SCALE || DEFAULT_SET_TIME);
        }
    };

    /* eslint-disable @typescript-eslint/member-ordering */
    private debounceStartLoopGif = debounce(this.startLoopGif, DEBOUNCE_LOOP_TIME);

    private getGifFrameResourceByIndex = async (
        imageDecoder: ImageDecoder,
        frameIndex: number,
    ): Promise<VideoFrame | undefined> => {
        try {
            if (this.gifDecoder?.type) {
                const frame = await imageDecoder.decode({ frameIndex });
                const { image } = frame;
                return image;
            }

            return this.currentVideoFrame;
        } catch (e) {
            return this.currentVideoFrame;
        }
    };

    /**
     * 是否在编辑操作中，编辑操作中会停止播放
     */
    private isNoOperation = () => !this.factoryOptions.editingStateManager?.isDragging
        && !this.factoryOptions.editingStateManager?.isResizing
        && !this.factoryOptions.editingStateManager?.isRotating
        && !this.factoryOptions.editingStateManager?.isCroping;

    /**
     * 是否是用户操作的播放/暂停gif
     */
    private isUserPlayGig = () => !(
        !(this.factoryOptions.model as IPictureModel).isPlaying
            && this.factoryOptions.region === RenderPostionType.CANVAS);

    /**
     * 是否是隐藏的gif，隐藏的gif需要停止播放
     */
    private isShow = () => !this.factoryOptions.model.hidden;

    /**
     * 是否可以播放gif
     */
    private canLoopGif = () =>
        this.isNoOperation() // 非操作中
        && this.isShow() // 未隐藏
        && !this.disposed // 未卸载，有一种可能是在卸载前，render请求已经出去了
        && this.isCurrentRegionGifModel() // 是当前区域的gif
        && this.isUserPlayGig() // 播放/暂停按钮
        && this.factoryOptions.gifService; // 支持gif的入口

    /**
     * 是否是当前展示页的gif
     * @param slideModel
     */
    private isCurrentRelationModel = (slideModel?: ISlideModel): boolean => {
        if (!slideModel) return false;

        if (!this.factoryOptions.masterLayoutEditService?.isMasterEditType) {
            return this.isCurrentRelationModelInEditor(slideModel);
        }

        return this.isCurrentRelationModelInMaster(slideModel);
    };

    /**
     * 是否是当前页相关的gif，比如母版gif
     * @param currentModel
     */
    private isCurrentRelationModelInEditor = (currentModel: ISlideModel): boolean => {
        const masterModel = currentModel.getRelationMasterModel();
        const layoutModel = currentModel.getRelationLayoutModel();
        return this.factoryOptions.slideModel === masterModel
            || this.factoryOptions.slideModel === layoutModel
            || this.factoryOptions.slideModel === currentModel;
    };

    private isSlideChild = (currentModel: ISlideModel, compareModel: IBaseModel | null, modelType: UIModelType) => {
        if (!compareModel) return false;

        if (compareModel.type !== modelType) {
            return this.isSlideChild(currentModel, compareModel.parent, modelType);
        }

        return true;
    };

    private isCurrentRelationModelInMaster = (currentModel: ISlideModel): boolean => {
        const relationMasterModel = currentModel.getRelationMasterModel();
        const relationLayoutModel = currentModel.getRelationLayoutModel();
        if (currentModel.isMaster) {
            return this.factoryOptions.slideModel === currentModel;
        }

        return this.factoryOptions.slideModel === relationMasterModel
            || this.factoryOptions.slideModel === relationLayoutModel;
    };


    /**
     * 是否是当前渲染区域，例如演示态下，编辑态的就不应该播
     */
    private isCurrentRegionGifModel = () => {
        if (this.factoryOptions.isTempElement) {
            return true;
        }

        if (this.factoryOptions.region === RenderPostionType.CANVAS) {
            return this.isEditorGif();
        }

        if (this.factoryOptions.region === RenderPostionType.PLAYER) {
            return this.isPlayerGif();
        }
    };

    /**
     * 是否是编辑区gif
     */
    private isEditorGif = () => this.isCurrentRelationModel(this.factoryOptions.selectionService?.currentSlide)
        && !this.factoryOptions.gifService?.isPlayerPlaying;

    /**
     * 是否是演示态gif
     */
    private isPlayerGif = () => this.isCurrentRelationModel(this.factoryOptions.gifService?.currentPlayingSlideModel)
        && this.factoryOptions.gifService?.isPlayerPlaying;

    /**
     * 编辑状态修改gif播放
     */
    private initOperationReaction = () => {
        this.operationReaction = reaction(() => [
            this.factoryOptions.editingStateManager?.isDragging,
            this.factoryOptions.editingStateManager?.isResizing,
            this.factoryOptions.editingStateManager?.isRotating,
            this.factoryOptions.editingStateManager?.isCroping,
        ], ([
            isDragging,
            isResizing,
            isRotating,
            isCroping,
        ]) => {
            if (this.factoryOptions.getApplication?.()) {
                this.factoryOptions.getApplication().allRefresh = true;
            }
            if (isDragging || isResizing || isRotating || isCroping) {
                this.pauseGifLoopRender();
            } else if (this.isCurrentRegionGifModel()) {
                // this.overPlayFlag = true;
                this.startLoopGif();
            }
        });
    };

    /**
     * 切页修改gif播放
     */
    private initSlideChangeReaction = () => {
        this.slideChangeReaction = reaction(() => [
            this.factoryOptions.selectionService?.currentSlide,
        ], ([
            currentSlide,
        ]) => {
            if (this.factoryOptions.gifService?.isPlayerPlaying) {
                this.pauseGifLoopRender();
                this.resetGifInfo();
            } else {
                if (!this.isCurrentRegionGifModel()) {
                    this.pauseGifLoopRender();
                    this.resetGifInfo();
                } else {
                    this.startLoopGif();
                }
            }
        });
    };

    /**
     * 进出演示态修改gif播放
     */
    private initPlayerChangeReaction = () => {
        this.slidePlayerChangeReaction = reaction(() => [
            this.factoryOptions.gifService?.currentPlayingSlideModel,
        ], ([
            currentSlide,
        ]) => {
            if (!this.factoryOptions.gifService?.isPlayerPlaying) {
                this.pauseGifLoopRender();
            } else {
                if (!this.isCurrentRegionGifModel()) {
                    this.pauseGifLoopRender();
                } else {
                    this.startLoopGif();
                }
            }
        });
    };

    /**
     * 演示态切页修改gif播放
     */
    private initSlidePlayStateChange = () => {
        this.slidePlayReaction = reaction(() => [
            this.factoryOptions.gifService?.isPlayerPlaying,
        ], ([
            isPlaying,
        ]) => {
            if (!this.isCurrentRegionGifModel()) {
                this.pauseGifLoopRender();
            } else {
                this.startLoopGif();
            }
        });
    };

    /**
     * 复用当前帧
     * @param gifGraphicsModel
     */
    private reuseCurrentFrame = (gifGraphicsModel: IGraphicsOptions) => {
        if (gifGraphicsModel.fillStyle?.imageFill && this.currentVideoFrame) {
            gifGraphicsModel.fillStyle.imageFill.gifFrameResource = this.currentVideoFrame;
        }
    };

    /**
     * 释放图片资源
     */
    private clearImageDecoder = () => {
        // 再下下帧释放解码器，避免下一帧使用这个
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                this.gifDecoder?.close?.();
                this.gifDecoder = undefined;
            });
        });
    };

    /**
     * 重置这个组件所有gif资源相关
     */
    public resetGifInfo = () => {
        this.clearImageDecoder();
        this.duration = 0;
    };

    private initGifPlayStateChange = () => {
        const pictureModel = this.factoryOptions.model as IPictureModel;
        this.gifPlayReaction = reaction(() => [
            pictureModel.isPlaying,
        ], ([
            isGifPlaying,
        ]) => {
            if (this.factoryOptions.region === RenderPostionType.CANVAS) {
                if (isGifPlaying) {
                    this.renderComponent();
                } else {
                    this.pauseGifLoopRender();
                }
            }
        });
    };

    /**
     * 更新gif fps，fps高于30帧时，抽一半
     * @param gifFrameResource
     * @private
     */
    private updateDuration(gifFrameResource: VideoFrame | undefined) {
        if (gifFrameResource) {
            this.duration = (gifFrameResource.duration || DEFAULT_DURATION);
            if (this.duration <= 33333) {
                this.duration = this.duration * 2;
                this.needFrameExtract = true;
            } else {
                this.needFrameExtract = false;
            }
        }
    }
}
